/**
 * Filial-Benchmark: eine Filiale vs. Durchschnitt aller anderen.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type BenchmarkResponse } from '../lib/api.ts';
import { useAvailableStores } from '../lib/useAvailableStores.ts';
import { DateRangePicker } from '../components/DateRangePicker.tsx';
import { ExportMenu } from '../components/ExportMenu.tsx';
import { TableSkeleton } from '../components/Skeleton.tsx';
import { fmtCurrency, fmtPercent } from '../lib/format.ts';
import { Icon } from '../components/Icon.tsx';

function todayMinus(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().substring(0, 10);
}

export function BenchmarkPage() {
  const availableStores = useAvailableStores();
  const [storeId, setStoreId] = useState<number | null>(null);
  const [periode, setPeriode] = useState({
    min: todayMinus(30),
    max: todayMinus(1),
  });

  useEffect(() => {
    if (storeId === null && availableStores.length > 0) {
      setStoreId(availableStores[0].id);
    }
  }, [availableStores, storeId]);

  const { data, isLoading } = useQuery({
    queryKey: ['benchmark', storeId, periode],
    queryFn: () =>
      api.benchmark({ store_id: storeId!, periode }),
    enabled: storeId !== null && !!periode.min && !!periode.max,
  });

  const selectedStore = availableStores.find((s) => s.id === storeId);

  return (
    <div className="space-y-4 lg:space-y-6 w-full">
      <div>
        <div className="section-head mb-1.5">
          <span className="section-eyebrow">Bericht</span>
          <div className="section-rule" />
        </div>
        <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Filial-Benchmark
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Eine Filiale vs. Durchschnitt aller anderen — pro Warengruppe.
        </p>
      </div>

      {/* Filter-Bar */}
      <div className="card card-body">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-2xs uppercase tracking-wider font-medium text-slate-500 mb-1">
              Filiale
            </label>
            <select
              value={storeId ?? ''}
              onChange={(e) => setStoreId(Number(e.target.value))}
              className="input"
            >
              {availableStores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.store_number} — {s.name}
                </option>
              ))}
            </select>
          </div>
          <DateRangePicker
            label="Zeitraum"
            value={periode}
            onChange={(v) => v.min && v.max && setPeriode({ min: v.min, max: v.max })}
          />
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {data && (
              <>
                Vergleich gegen{' '}
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  {data.anzahl_vergleichsfilialen}
                </span>{' '}
                andere Filialen
              </>
            )}
          </div>
        </div>
      </div>

      {/* Total-Karten */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 animate-fade-in">
          <div className="kpi-tile">
            <div className="kpi-label">Eigener Umsatz</div>
            <div className="kpi-value">{fmtCurrency(data.total_eigen)}</div>
            <div className="text-2xs text-slate-500 mt-1">
              {selectedStore?.name}
            </div>
          </div>
          <div className="kpi-tile">
            <div className="kpi-label">Durchschnitt anderer</div>
            <div className="kpi-value text-slate-500">
              {fmtCurrency(data.total_schnitt)}
            </div>
            <div className="text-2xs text-slate-500 mt-1">
              Schnitt {data.anzahl_vergleichsfilialen} Filialen
            </div>
          </div>
          <div className="kpi-tile">
            <div className="kpi-label">Differenz</div>
            <div
              className={`kpi-value ${
                data.total_eigen >= data.total_schnitt
                  ? 'kpi-delta-up'
                  : 'kpi-delta-down'
              }`}
            >
              {data.total_eigen >= data.total_schnitt ? '+' : ''}
              {fmtCurrency(data.total_eigen - data.total_schnitt)}
            </div>
          </div>
          <div className="kpi-tile">
            <div className="kpi-label">Differenz %</div>
            <div
              className={`kpi-value flex items-center gap-2 ${
                (data.total_diff_pct ?? 0) >= 0 ? 'kpi-delta-up' : 'kpi-delta-down'
              }`}
            >
              <Icon
                name={(data.total_diff_pct ?? 0) >= 0 ? 'trendingUp' : 'trendingDown'}
                size={20}
              />
              {data.total_diff_pct !== null
                ? `${data.total_diff_pct >= 0 ? '+' : ''}${fmtPercent(data.total_diff_pct)}`
                : '—'}
            </div>
          </div>
        </div>
      )}

      {/* Tabelle */}
      <div className="card">
        <div className="card-header">
          <div>
            <h3 className="card-title">Detail nach Warengruppe</h3>
            <p className="card-subtitle">
              Sortiert nach eigenem Umsatz — abweichende Bereiche schnell erkennen
            </p>
          </div>
          {data && (
            <ExportMenu
              rows={data.rows as unknown as Record<string, unknown>[]}
              columns={[
                { header: 'Warengruppe', key: 'warengruppe_name', width: 40 },
                {
                  header: 'Eigen (€)',
                  key: 'umsatz_eigen',
                  numeric: true,
                  format: (v) => fmtCurrency(Number(v)),
                },
                {
                  header: 'Schnitt (€)',
                  key: 'umsatz_schnitt',
                  numeric: true,
                  format: (v) => fmtCurrency(Number(v)),
                },
                {
                  header: 'Δ abs (€)',
                  key: 'diff_abs',
                  numeric: true,
                  format: (v) => fmtCurrency(Number(v)),
                },
                {
                  header: 'Δ %',
                  key: 'diff_pct',
                  format: (v) => (v === null ? '' : fmtPercent(Number(v))),
                },
              ]}
              filename={`Benchmark-${selectedStore?.store_number}-${periode.min}_${periode.max}`}
              title={`Filial-Benchmark: ${selectedStore?.name ?? ''}`}
              subtitle={`Zeitraum ${periode.min} bis ${periode.max} — Vergleich gegen ${data.anzahl_vergleichsfilialen} andere Filialen`}
            />
          )}
        </div>
        <div className="card-body">
          {isLoading ? (
            <TableSkeleton rows={10} />
          ) : !data || data.rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">
              Keine Daten im gewählten Zeitraum.
            </div>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Warengruppe</th>
                    <th className="num">Eigen</th>
                    <th className="num">Schnitt</th>
                    <th className="num">Δ Absolut</th>
                    <th className="num">Δ Prozent</th>
                    <th className="w-24"><span className="sr-only">Indikator</span></th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r: BenchmarkResponse['rows'][number]) => {
                    const positive = r.diff_abs >= 0;
                    const pct = r.diff_pct ?? 0;
                    const intensity = Math.min(Math.abs(pct) * 2, 1); // 0..1
                    return (
                      <tr key={r.warengruppe_name}>
                        <td className="font-medium truncate max-w-xs">
                          {r.warengruppe_name}
                        </td>
                        <td className="num font-semibold">{fmtCurrency(r.umsatz_eigen)}</td>
                        <td className="num text-slate-500">{fmtCurrency(r.umsatz_schnitt)}</td>
                        <td
                          className={`num font-semibold ${
                            positive ? 'kpi-delta-up' : 'kpi-delta-down'
                          }`}
                        >
                          {positive ? '+' : ''}
                          {fmtCurrency(r.diff_abs)}
                        </td>
                        <td
                          className={`num font-semibold ${
                            positive ? 'kpi-delta-up' : 'kpi-delta-down'
                          }`}
                        >
                          {r.diff_pct !== null
                            ? `${positive ? '+' : ''}${fmtPercent(r.diff_pct)}`
                            : '—'}
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <div
                              className={`h-1.5 flex-1 rounded-full ${
                                positive
                                  ? 'bg-positive-500/20'
                                  : 'bg-accent-500/20'
                              }`}
                            >
                              <div
                                className={`h-full rounded-full ${
                                  positive ? 'bg-positive-500' : 'bg-accent-500'
                                }`}
                                style={{ width: `${intensity * 100}%` }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
